import React, { useEffect, useMemo, useState } from 'react';

import { styled } from 'styled-components';

import ClearIcon from '@mui/icons-material/Clear';

import { MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import FilterBetween from 'images/filter-between.svg';
import FilterEquals from 'images/filter-equals.svg';
import FilterGreaterThan from 'images/filter-grater-than.svg';
import FilterGreaterThanOrEqual from 'images/filter-greater-than-or-equal.svg';
import FilterLessThanOrEqual from 'images/filter-less-than-or-equal.svg';
import FilterLessThan from 'images/filter-less-than.svg';
import FilterNoEquals from 'images/filter-no-equals.svg';
import { useTranslation } from 'react-i18next';

import { ColumnFilteringProps } from '../types';

import { _, dayjs } from 'third-party';

import { USER_INPUT_DEBOUNCE_TIME_MS } from 'constants/common';

import {
  APIDateTimeOperators,
  APIDateTimeSearchFilter,
} from 'types/swaggerApi';

const RESET_FILTER = '__reset__';

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  padding: 0.1rem 0 0.3rem 0;

  & input {
    padding: 0.1rem !important;
  }
`;

const ConditionSelect = styled(Select)`
  min-width: 2.5rem;
  max-width: 4rem;
  font-size: 0.75rem;

  & .MuiSelect-select {
    display: flex;
    align-items: center;
    padding: 0.1rem 0.25rem !important;
  }
`;

const FilterIcon = styled.img`
  width: 16px;
  height: 16px;
  filter: invert(32%) sepia(95%) saturate(1200%) hue-rotate(162deg)
    brightness(88%) contrast(101%);
`;

const RangeSeparator = styled.span`
  align-self: center;
  font-size: 0.75rem;
`;

const datePickerStyles = `
  flex-grow: 1;

  & .MuiInputBase-root {
    font-size: 0.75rem;
  }

  & .MuiInputBase-input {
    padding: 0.25rem 0.5rem !important;
  }

  & .MuiInputAdornment-root {
    margin-left: 0;
  }

  & .MuiIconButton-root {
    padding: 0.1rem;
  }

  & .MuiSvgIcon-root {
    font-size: 1rem;
  }
`;

const StyledDatePicker = styled(DesktopDatePicker)`
  ${datePickerStyles}
`;

const StyledDateTimePicker = styled(DesktopDateTimePicker)`
  ${datePickerStyles}
`;

type OperatorConfig = {
  icon: string | null;
  label: string;
  i18nKey: string;
};

const operatorConfig: Partial<Record<APIDateTimeOperators, OperatorConfig>> = {
  [APIDateTimeOperators.Equals]: {
    icon: FilterEquals,
    label: '=',
    i18nKey: 'filter.date.equals',
  },
  [APIDateTimeOperators.NotEquals]: {
    icon: FilterNoEquals,
    label: '≠',
    i18nKey: 'filter.date.notEquals',
  },
  [APIDateTimeOperators.GreaterThan]: {
    icon: FilterGreaterThan,
    label: '>',
    i18nKey: 'filter.date.greaterThan',
  },
  [APIDateTimeOperators.GreaterThanOrEqual]: {
    icon: FilterGreaterThanOrEqual,
    label: '≥',
    i18nKey: 'filter.date.greaterThanOrEqual',
  },
  [APIDateTimeOperators.LessThan]: {
    icon: FilterLessThan,
    label: '<',
    i18nKey: 'filter.date.lessThan',
  },
  [APIDateTimeOperators.LessThanOrEqual]: {
    icon: FilterLessThanOrEqual,
    label: '≤',
    i18nKey: 'filter.date.lessThanOrEqual',
  },
  [APIDateTimeOperators.Between]: {
    icon: FilterBetween,
    label: '⟷',
    i18nKey: 'filter.date.between',
  },
};

type AdvancedDateFilterProps = ColumnFilteringProps & {
  dateOnly?: boolean;
};

export const AdvancedDateFilter: React.FC<AdvancedDateFilterProps> = ({
  columnId,
  filterValue,
  onAdvancedFilterChange,
  dateOnly = false,
}) => {
  const { t } = useTranslation();

  const currentFilter = filterValue as APIDateTimeSearchFilter | undefined;

  const [condition, setCondition] = useState<APIDateTimeOperators>(
    currentFilter?.condition || APIDateTimeOperators.Equals,
  );
  const [value, setValue] = useState<string | null>(
    currentFilter?.value || null,
  );
  const [valueTo, setValueTo] = useState<string | null>(
    currentFilter?.valueTo || null,
  );

  const debouncedEmitChange = useMemo(
    () =>
      _.debounce(
        (
          newCondition: APIDateTimeOperators,
          newValue: string | null,
          newValueTo: string | null,
        ) => {
          const filter: APIDateTimeSearchFilter = {
            condition: newCondition,
            value: newValue,
            valueTo:
              newCondition === APIDateTimeOperators.Between
                ? newValueTo
                : undefined,
          };
          onAdvancedFilterChange?.(columnId.toString(), filter);
        },
        USER_INPUT_DEBOUNCE_TIME_MS,
      ),
    [columnId, onAdvancedFilterChange],
  );

  const handleConditionChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string;

    if (selectedValue === RESET_FILTER) {
      setCondition(APIDateTimeOperators.Equals);
      setValue(null);
      setValueTo(null);
      debouncedEmitChange.cancel();
      onAdvancedFilterChange?.(columnId.toString(), undefined);
      return;
    }

    const newCondition = selectedValue as APIDateTimeOperators;
    setCondition(newCondition);
    debouncedEmitChange(newCondition, value, valueTo);
  };

  const formatDateValue = (date: dayjs.Dayjs | null): string | null => {
    if (!date) return null;
    // For dateOnly mode, send YYYY-MM-DD format; otherwise send ISO string
    return dateOnly ? date.format('YYYY-MM-DD') : date.toISOString();
  };

  const handleValueChange = (newValue: dayjs.Dayjs | null) => {
    const formattedValue = formatDateValue(newValue);
    setValue(formattedValue);
    debouncedEmitChange(condition, formattedValue, valueTo);
  };

  const handleValueToChange = (newValue: dayjs.Dayjs | null) => {
    const formattedValue = formatDateValue(newValue);
    setValueTo(formattedValue);
    debouncedEmitChange(condition, value, formattedValue);
  };

  useEffect(() => {
    if (currentFilter) {
      setCondition(currentFilter.condition || APIDateTimeOperators.Equals);
      setValue(currentFilter.value || null);
      setValueTo(currentFilter.valueTo || null);
    } else {
      setCondition(APIDateTimeOperators.Equals);
      setValue(null);
      setValueTo(null);
    }
  }, [currentFilter]);

  const showRangeInput = condition === APIDateTimeOperators.Between;

  return (
    <FilterWrapper>
      <ConditionSelect
        size="small"
        variant="standard"
        value={condition}
        onChange={handleConditionChange}
        sx={{
          '& legend': { display: 'none' },
          '& fieldset': { top: 0 },
        }}
      >
        <MenuItem value={RESET_FILTER} sx={{ fontSize: '0.875rem' }}>
          <Tooltip title={t('filter.clear' as any)} placement="right">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <ClearIcon sx={{ width: 16, height: 16, color: '#007170' }} />
            </span>
          </Tooltip>
        </MenuItem>
        {Object.entries(operatorConfig).map(([op, config]) => (
          <MenuItem key={op} value={op} sx={{ fontSize: '0.875rem' }}>
            <Tooltip title={t(config.i18nKey as any)} placement="right">
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {config.icon ? (
                  <FilterIcon src={config.icon} alt={config.label} />
                ) : (
                  config.label
                )}
              </span>
            </Tooltip>
          </MenuItem>
        ))}
      </ConditionSelect>
      {dateOnly ? (
        <StyledDatePicker
          value={value ? dayjs(value) : null}
          onChange={handleValueChange}
          slotProps={{
            textField: {
              size: 'small',
              variant: 'standard',
              sx: { maxWidth: showRangeInput ? '6rem' : undefined },
            },
          }}
          format="MM/DD/YYYY"
        />
      ) : (
        <StyledDateTimePicker
          value={value ? dayjs(value) : null}
          onChange={handleValueChange}
          slotProps={{
            textField: {
              size: 'small',
              variant: 'standard',
              sx: { maxWidth: showRangeInput ? '7rem' : undefined },
            },
          }}
          format="MM/DD/YYYY HH:mm"
        />
      )}
      {showRangeInput && (
        <>
          <RangeSeparator>-</RangeSeparator>
          {dateOnly ? (
            <StyledDatePicker
              value={valueTo ? dayjs(valueTo) : null}
              onChange={handleValueToChange}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  sx: { maxWidth: '6rem' },
                },
              }}
              format="MM/DD/YYYY"
            />
          ) : (
            <StyledDateTimePicker
              value={valueTo ? dayjs(valueTo) : null}
              onChange={handleValueToChange}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  sx: { maxWidth: '7rem' },
                },
              }}
              format="MM/DD/YYYY HH:mm"
            />
          )}
        </>
      )}
    </FilterWrapper>
  );
};
